import React, { Dispatch, ReactNode, useEffect, useRef, useState } from 'react';
import { TextInput } from '@sso/components';
import { ConditionalProps } from '@sso/components/src/TextInput';
import '@ux/text-input/styles';
import { shallowEqual, useSelector } from 'react-redux';
import { AnyAction } from 'redux';

import ShowHideButton from '../ShowHideButton';
import EditCancelButton from '../EditCancelButton/index';

type InlineLabelInputTextInputProps = {
  id?: string;
  label: string;
  value?: string;
  type?: ConditionalProps['type'];
  focus?: boolean;
  message?: string;
  className?: string;
  isError?: boolean;
  onChange: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress: (_event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur: (_event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus: (_event: React.FocusEvent<HTMLInputElement>) => void;
  showInlineInputs?: boolean;
  isShowHidePassword?: boolean;
  isEditCancelEmail?: boolean;
  editRecoveryEmail?: boolean;
  dispatch?: Dispatch<AnyAction>;
  readOnly?: boolean;
  required?: boolean;
  autoComplete?: string;
  ctaAddOn?: ReactNode | undefined;
  showButtonText?: string;
  hideButtonText?: string;
};

interface RootState {
  globals: {
    localize: (_key: string) => string;
  };
}

const InlineLabelInputTextInput: React.FC<InlineLabelInputTextInputProps> = props => {
  const {
    type,
    focus,
    message,
    isError,
    onChange,
    showInlineInputs = true,
    isShowHidePassword,
    isEditCancelEmail,
    editRecoveryEmail,
    dispatch,
    readOnly,
    ...restProps
  } = props;

  const [isShow, setIsShow] = useState(false);
  const [isEdit, setIsEdit] = useState(editRecoveryEmail ?? false);
  const localize = useSelector((state: RootState) => state.globals.localize, shallowEqual);

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focus ?? false) {
      ref.current?.focus();
    }
  }, [focus]);

  useEffect(() => {
    setIsEdit(editRecoveryEmail ?? false);
  }, [editRecoveryEmail]);

  const handleShowHideClick = () => setIsShow(isShow => !isShow);
  const customType = isShow ? 'text' : 'password';
  const handleEditCancelClick = () => setIsEdit(isEdit => !isEdit);
  const inputType: ConditionalProps['type'] = isShowHidePassword === true ? customType : type;
  return (
    <>
      <TextInput
        {...restProps}
        invalid={isError}
        ref={ref}
        onChange={(_value: string, { event }: { event: React.ChangeEvent<HTMLInputElement> }) => onChange(event)}
        errorMessage={message}
        labelExperimentalFloat={showInlineInputs}
        disabled={(readOnly === true || isEditCancelEmail === true) && !isEdit}
        {...(inputType === 'password'
          ? {
              type: inputType,
              showButtonText: props.showButtonText ?? '',
              hideButtonText: props.hideButtonText ?? '',
            }
          : { type: inputType })}
      />
      {isShowHidePassword === true && (
        <ShowHideButton
          show_text={localize('SHOW')}
          hide_text={localize('HIDE')}
          handleClick={handleShowHideClick}
          show_password={isShow}
          aria_show_text={localize('SHOW_PASSWORD')}
          aria_hide_text={localize('HIDE_PASSWORD')}
          showInlineInputs={showInlineInputs}
        />
      )}
      {isEditCancelEmail === true && (
        <EditCancelButton
          edit_text={localize('EDIT')}
          cancel_text={localize('CANCEL')}
          handleClick={handleEditCancelClick}
          edit_email={isEdit}
          aria_edit_text={localize('EDIT')}
          aria_cancel_text={localize('CANCEL')}
        />
      )}
    </>
  );
};

export default InlineLabelInputTextInput;
